import React, { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { LEGAL, TERMS_DATE } from "../legal/config";
import { COMMISSION_RATE } from "../utils/pricing";

// Pages légales : mentions légales, CGV et politique de confidentialité.
// Les informations sur l'exploitant viennent de src/legal/config.js.

const COMMISSION = `${Math.round(COMMISSION_RATE * 100)} %`;

// Valeur de config.js, ou encadré « à compléter » tant qu'elle manque
const Fill = ({ value, label }) =>
  value ? (
    <>{value}</>
  ) : (
    <span className="inline-block px-1.5 rounded bg-or-light text-prune-deep border border-or font-semibold">
      [À compléter : {label}]
    </span>
  );

const Email = () =>
  LEGAL.contactEmail ? (
    <a href={`mailto:${LEGAL.contactEmail}`} className="text-prune underline">{LEGAL.contactEmail}</a>
  ) : (
    <Fill label="e-mail de contact" />
  );

const Operator = () => <Fill value={LEGAL.operatorName} label="nom de l'exploitant" />;

const H2 = ({ id, children }) => (
  <h2 id={id} className="font-display font-bold text-2xl text-prune-deep mt-10 mb-3 scroll-mt-24">{children}</h2>
);
const H3 = ({ children }) => <h3 className="font-display font-bold text-lg text-prune-deep mt-6 mb-2">{children}</h3>;
const P = ({ children }) => <p className="mb-3 leading-relaxed">{children}</p>;
const Ul = ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1.5 leading-relaxed">{children}</ul>;
const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="text-prune underline">{children}</a>
);

const DOCS = [
  { to: "/mentions-legales", label: "Mentions légales" },
  { to: "/cgv", label: "Conditions générales" },
  { to: "/confidentialite", label: "Confidentialité" },
];

const LegalLayout = ({ title, children }) => {
  const { t, lang } = useLang();
  const { pathname, hash } = useLocation();
  // Haut de page, ou la section demandée (ex. /cgv#pieces)
  useEffect(() => {
    const target = hash && document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-10 md:py-14">
        <nav aria-label={t("Documents légaux")} className="flex flex-wrap gap-2 mb-8">
          {DOCS.map((d) => (
            <NavLink
              key={d.to}
              to={d.to}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  isActive ? "bg-prune text-white border-prune" : "bg-white text-prune border-lin hover:border-prune"
                }`
              }
            >
              {t(d.label)}
            </NavLink>
          ))}
        </nav>

        {lang !== "fr" && (
          <p className="mb-6 px-4 py-3 rounded-xl bg-white border border-lin text-sm text-stone-700" lang={lang}>
            {t("Ce document n'existe pour l'instant qu'en français. La version française fait foi.")}
          </p>
        )}

        <article lang="fr" className="bg-white rounded-3xl border border-lin shadow-carte p-6 sm:p-10 text-stone-700">
          <h1 className="font-display font-bold text-3xl md:text-4xl text-prune-deep mb-2">{title}</h1>
          <p className="text-sm text-stone-600 mb-6">Dernière mise à jour : {TERMS_DATE}</p>
          {children}
        </article>
      </main>
      <Footer />
    </div>
  );
};

/* ─────────────────────────── Mentions légales ─────────────────────────── */

export const MentionsLegales = () => (
  <LegalLayout title="Mentions légales">
    <H2 id="editeur">Éditeur du site</H2>
    <P>Le site Tatie Soso est exploité par :</P>
    <Ul>
      <li><strong>Nom :</strong> <Operator /></li>
      <li><strong>Forme juridique :</strong> <Fill value={LEGAL.legalForm} label="forme juridique" /></li>
      <li><strong>Adresse :</strong> <Fill value={LEGAL.address} label="adresse du siège" /></li>
      <li><strong>Numéro d'entreprise (BCE) :</strong> <Fill value={LEGAL.companyNumber} label="numéro BCE" /></li>
      <li><strong>TVA :</strong> <Fill value={LEGAL.vatNumber} label="numéro de TVA" /></li>
      <li><strong>E-mail :</strong> <Email /></li>
      {LEGAL.phone && <li><strong>Téléphone :</strong> {LEGAL.phone}</li>}
      <li><strong>Responsable de la publication :</strong> <Fill value={LEGAL.publisher} label="responsable de la publication" /></li>
    </Ul>

    <H2 id="hebergement">Hébergement</H2>
    <P>
      Le site et ses données sont hébergés par Google, via le service Firebase : Google Ireland Limited, Gordon House,
      Barrow Street, Dublin 4, Irlande.
    </P>

    <H2 id="propriete">Propriété intellectuelle</H2>
    <P>
      Le nom Tatie Soso, le logo, les illustrations, les textes et la mise en page du site appartiennent à <Operator />.
      Toute reproduction sans autorisation écrite est interdite. Les descriptions de cours, photos et contenus publiés
      par les professeurs restent leur propriété ; ils sont responsables de leur contenu.
    </P>

    <H2 id="signalement">Signaler un contenu illicite</H2>
    <P>
      Si tu vois sur Tatie Soso un contenu ou un comportement illicite ou inapproprié (en particulier envers un
      mineur), écris-nous à <Email /> en indiquant la page concernée et ce qui pose problème. Nous examinons chaque
      signalement rapidement et retirons le contenu si nécessaire.
    </P>

    <H2 id="documents">Autres documents</H2>
    <P>
      Consulte aussi nos <Link to="/cgv" className="text-prune underline">conditions générales</Link> et notre{" "}
      <Link to="/confidentialite" className="text-prune underline">politique de confidentialité</Link>.
    </P>
  </LegalLayout>
);

/* ───────────────────── Conditions générales (CGV / CGU) ───────────────────── */

export const CGV = () => (
  <LegalLayout title="Conditions générales d'utilisation et de vente">
    <P>
      Ces conditions s'appliquent à toute utilisation du site Tatie Soso et à tout achat de pièces ou inscription à
      un cours. En créant un compte, tu les acceptes. Si tu as moins de 18 ans, lis-les avec ton parent ou ton
      tuteur légal.
    </P>

    <H2 id="role">1. Qui fait quoi ?</H2>
    <Ul>
      <li>
        <strong>Tatie Soso</strong> (<Operator />, voir les <Link to="/mentions-legales" className="text-prune underline">mentions légales</Link>)
        exploite la plateforme : elle permet de trouver des cours, vend les pièces qui servent à payer les cours, gère
        les inscriptions, les remboursements et le paiement des professeurs.
      </li>
      <li>
        <strong>Les professeurs</strong> créent et donnent les cours. Ils sont indépendants de Tatie Soso et
        responsables du contenu et du déroulement de leurs cours.
      </li>
      <li>
        Sauf mention contraire sur sa fiche, un professeur agit en tant que particulier et non en tant que
        professionnel. Les règles de protection des consommateurs ne s'appliquent alors pas au contrat conclu avec
        lui. Tatie Soso reste responsable envers toi de ce qu'elle fournit elle-même : la plateforme, les pièces et
        les remboursements prévus dans ces conditions.
      </li>
    </Ul>

    <H2 id="compte">2. Ton compte</H2>
    <Ul>
      <li>Le compte se crée avec un compte Google. Une personne = un compte.</li>
      <li>Les informations que tu donnes (nom, date de naissance…) doivent être exactes.</li>
      <li>Tu es responsable de ce qui est fait avec ton compte : garde ton accès Google pour toi.</li>
      <li>Tu peux fermer ton compte à tout moment (voir l'article 11).</li>
    </Ul>
    <H3>Mineurs</H3>
    <Ul>
      <li>
        <strong>Moins de 13 ans :</strong> le compte doit être créé par le parent ou le tuteur légal, qui accepte ces
        conditions pour l'enfant et reste responsable de son utilisation du site.
      </li>
      <li>
        <strong>De 13 à 17 ans :</strong> l'inscription et les achats demandent l'accord du parent ou du tuteur légal.
        En confirmant cet accord lors de l'inscription, tu déclares l'avoir obtenu.
      </li>
    </Ul>

    <H2 id="pieces">3. Les pièces</H2>
    <Ul>
      <li>Les cours se paient en pièces. <strong>1 pièce = 1 €</strong>, toutes taxes comprises. Leur valeur ne varie pas.</li>
      <li>Les pièces s'achètent sur la page Portefeuille, par PayPal, de 1 à 1 000 € par achat. Le paiement est traité par PayPal : Tatie Soso ne voit jamais tes coordonnées bancaires.</li>
      <li>Les pièces sont créditées dès que PayPal confirme le paiement. Elles n'expirent pas et ne rapportent pas d'intérêts.</li>
      <li>Elles ne s'utilisent que sur Tatie Soso. Tu peux en transférer à un autre utilisateur depuis ton portefeuille ; un transfert confirmé ne peut pas être annulé par Tatie Soso, vérifie donc l'adresse e-mail du destinataire.</li>
      <li>Les pièces non utilisées sont remboursées sur simple demande à <Email />, sur le moyen de paiement d'origine, dans les 14 jours suivant la demande.</li>
    </Ul>

    <H2 id="inscription">4. S'inscrire à un cours : le prix partagé</H2>
    <Ul>
      <li>Chaque cours a un prix total, fixé par le professeur, et un nombre maximum d'élèves.</li>
      <li>Ce prix est partagé entre les inscrits. Quand tu t'inscris, tu paies <em>prix total ÷ nombre d'inscrits (toi compris)</em> ; le montant exact est affiché avant que tu confirmes.</li>
      <li>À chaque nouvel inscrit, la part de chacun baisse : la différence est recréditée automatiquement en pièces sur ton portefeuille. Ta part ne peut jamais augmenter après ton inscription.</li>
      <li>L'inscription est confirmée dès que les pièces sont débitées. Le lien de la visio apparaît dans « Mes cours » 15 minutes avant le début.</li>
    </Ul>

    <H2 id="retractation">5. Droit de rétractation (14 jours)</H2>
    <P>Si tu achètes en tant que consommateur, tu disposes d'un droit de rétractation de 14 jours, sans avoir à te justifier :</P>
    <Ul>
      <li><strong>Achat de pièces :</strong> 14 jours à partir de l'achat. Les pièces non utilisées te sont remboursées dans les 14 jours suivant ta demande.</li>
      <li>
        <strong>Inscription à un cours :</strong> 14 jours à partir de l'inscription, et au plus tard jusqu'au début
        du cours. Si le cours a lieu pendant ce délai, tu demandes expressément, au moment de l'inscription, qu'il
        soit donné à la date prévue, et tu reconnais perdre ton droit de rétractation une fois le cours entièrement
        donné (article VI.53, 1° du Code de droit économique).
      </li>
    </Ul>
    <P>
      Pour te rétracter, envoie une déclaration claire à <Email /> (tu peux utiliser le modèle en bas de page). Le
      remboursement se fait en pièces pour une inscription, et sur le moyen de paiement d'origine pour un achat de
      pièces, sans frais.
    </P>

    <H2 id="annulation">6. Annulation d'un cours</H2>
    <Ul>
      <li><strong>Par le professeur ou par Tatie Soso :</strong> tous les élèves inscrits sont remboursés intégralement, en pièces.</li>
      <li><strong>Par toi, après le délai de rétractation :</strong> tu peux annuler sans frais jusqu'à 24 heures avant le début du cours en écrivant à <Email /> ; ce que tu as payé est recrédité en pièces. Moins de 24 heures avant, ou en cas d'absence, le cours reste dû.</li>
      <li><strong>Problème technique :</strong> si le cours n'a pas pu avoir lieu à cause de la plateforme ou du professeur, écris-nous : tu seras remboursé·e.</li>
    </Ul>

    <H2 id="profs">7. Conditions propres aux professeurs</H2>
    <Ul>
      <li>Le professeur fixe le prix total, la date, la durée et le nombre de places de ses cours, et s'engage à les donner comme annoncés.</li>
      <li>Après la fin du cours, il récupère sur son portefeuille les pièces payées par les élèves, moins une commission de {COMMISSION} pour Tatie Soso.</li>
      <li>Il s'occupe lui-même de ses obligations fiscales et sociales (déclaration des revenus, statut d'indépendant si nécessaire).</li>
      <li>Il garantit que ses contenus (textes, photos, supports) ne violent les droits de personne et respectent la loi.</li>
      <li>S'il supprime un cours avant qu'il ait eu lieu, les élèves inscrits sont remboursés automatiquement.</li>
    </Ul>

    <H2 id="conduite">8. Règles de bonne conduite</H2>
    <Ul>
      <li>Respect et bienveillance envers tous : pas d'insultes, de harcèlement, de discrimination ni de contenu choquant.</li>
      <li>Une attention particulière est due aux élèves mineurs : aucun contact privé déplacé, aucune demande d'informations personnelles.</li>
      <li>Il est interdit d'enregistrer ou de filmer un cours sans l'accord de tous les participants (et de leurs parents pour les mineurs).</li>
      <li>Les liens de visio sont réservés aux inscrits : ne les partage pas.</li>
    </Ul>
    <P>En cas de manquement, Tatie Soso peut retirer un contenu, suspendre ou fermer le compte concerné.</P>

    <H2 id="visio">9. Visioconférence</H2>
    <P>
      Les cours ont lieu en ligne, dans une salle Jitsi Meet créée par Tatie Soso ou via le lien choisi par le
      professeur (par exemple Google Meet ou Zoom). Ces services sont fournis par des tiers, selon leurs propres
      conditions.
    </P>

    <H2 id="responsabilite">10. Responsabilité</H2>
    <Ul>
      <li>Tatie Soso fait de son mieux pour que le site fonctionne en continu, sans pouvoir le garantir (maintenance, panne d'un prestataire…).</li>
      <li>Tatie Soso n'est pas l'auteur des cours et ne répond pas de leur contenu pédagogique, sauf faute de sa part.</li>
      <li>Rien dans ces conditions ne limite les droits que la loi te garantit, ni la responsabilité de Tatie Soso en cas de faute grave ou intentionnelle.</li>
    </Ul>

    <H2 id="fermeture">11. Fermeture du compte</H2>
    <P>
      Tu peux demander la fermeture de ton compte à tout moment depuis la page « Mon compte » ou par e-mail. Tes pièces
      non utilisées te sont alors remboursées. Tatie Soso peut fermer un compte en cas de manquement grave à ces
      conditions, après t'avoir prévenu·e sauf urgence ; les pièces achetées et non utilisées sont alors aussi
      remboursées.
    </P>

    <H2 id="reclamations">12. Réclamations et médiation</H2>
    <P>
      Pour toute question ou réclamation, écris-nous à <Email /> : nous répondons dans les meilleurs délais. Si nous
      ne trouvons pas de solution ensemble, tu peux t'adresser gratuitement au Service de Médiation pour le
      Consommateur, North Gate II, boulevard du Roi Albert II 8 boîte 1, 1000 Bruxelles —{" "}
      <Ext href="https://www.mediationconsommateur.be">www.mediationconsommateur.be</Ext>.
    </P>

    <H2 id="droit">13. Droit applicable</H2>
    <P>
      Ces conditions sont soumises au droit belge. Si tu es consommateur, tu gardes la protection des règles
      impératives du pays où tu résides, et tu peux saisir les tribunaux de ton domicile.
    </P>

    <H2 id="modifications">14. Modifications</H2>
    <P>
      Nous pouvons modifier ces conditions. Les changements importants sont annoncés sur le site avant d'entrer en
      vigueur ; la version applicable à un achat est celle en vigueur au moment de cet achat.
    </P>

    <H2 id="formulaire">Annexe — Modèle de formulaire de rétractation</H2>
    <p className="text-sm text-stone-600 mb-3">(à compléter et renvoyer uniquement si tu veux te rétracter)</p>
    <div className="rounded-2xl bg-creme border border-lin p-5 text-sm leading-relaxed">
      <p className="mb-2">À l'attention de <Operator />, <Fill value={LEGAL.address} label="adresse" />, <Email /> :</p>
      <p className="mb-2">
        Je vous notifie par la présente ma rétractation du contrat portant sur l'achat de pièces / l'inscription au
        cours suivant(e) : …………
      </p>
      <p className="mb-2">Achat ou inscription du : …………</p>
      <p className="mb-2">Nom et adresse e-mail du compte : …………</p>
      <p>Date : …………</p>
    </div>
  </LegalLayout>
);

/* ─────────────────────── Politique de confidentialité ─────────────────────── */

const Row = ({ what, why, basis }) => (
  <tr className="border-t border-lin align-top">
    <td className="py-2 pr-3">{what}</td>
    <td className="py-2 pr-3">{why}</td>
    <td className="py-2">{basis}</td>
  </tr>
);

export const Confidentialite = () => (
  <LegalLayout title="Politique de confidentialité">
    <P>
      Cette page explique quelles données personnelles Tatie Soso utilise, pourquoi, avec qui elles sont partagées et
      comment exercer tes droits, conformément au Règlement général sur la protection des données (RGPD).
    </P>

    <H2 id="responsable">1. Responsable du traitement</H2>
    <P>
      <Operator />, <Fill value={LEGAL.address} label="adresse" />. Pour toute question sur tes données :{" "}
      <Email />.
    </P>

    <H2 id="donnees">2. Les données que nous utilisons</H2>
    <Ul>
      <li><strong>Compte :</strong> prénom, nom, adresse e-mail (de ton compte Google), date de naissance, rôle (élève ou professeur), date de création, date d'acceptation des conditions et, pour les mineurs, l'accord parental déclaré.</li>
      <li><strong>Profil :</strong> photo et description, si tu les ajoutes.</li>
      <li><strong>Portefeuille :</strong> solde de pièces, montants achetés via PayPal, transferts de pièces (adresse e-mail du destinataire).</li>
      <li><strong>Cours :</strong> cours auxquels tu es inscrit·e et montant payé ; pour les professeurs, les cours créés et les paiements reçus.</li>
      <li><strong>Données techniques :</strong> identifiant de connexion et journaux techniques tenus par notre hébergeur pour la sécurité du service.</li>
    </Ul>
    <P>Nous ne recevons jamais tes coordonnées bancaires : elles restent chez PayPal.</P>

    <H2 id="finalites">3. Pourquoi et sur quelle base</H2>
    <div className="overflow-x-auto mb-3">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-prune-deep">
            <th className="py-2 pr-3">Données</th>
            <th className="py-2 pr-3">Utilisation</th>
            <th className="py-2">Base légale</th>
          </tr>
        </thead>
        <tbody>
          <Row what="Compte, profil, cours" why="Créer ton compte, t'inscrire aux cours, organiser les cours" basis="Exécution du contrat (art. 6.1.b RGPD)" />
          <Row what="Date de naissance, accord parental" why="Protéger les mineurs et vérifier l'accord parental" basis="Obligation légale et exécution du contrat" />
          <Row what="Portefeuille, achats, paiements" why="Gérer les pièces, les remboursements et le paiement des profs ; tenir la comptabilité" basis="Exécution du contrat ; obligation légale (comptabilité)" />
          <Row what="Profil public des professeurs" why="Présenter les professeurs aux élèves" basis="Exécution du contrat (avec le professeur)" />
          <Row what="Données techniques" why="Sécurité du site, prévention de la fraude" basis="Intérêt légitime (art. 6.1.f RGPD)" />
        </tbody>
      </table>
    </div>
    <P>Nous ne faisons ni publicité ciblée, ni profilage, et nous ne vendons aucune donnée.</P>

    <H2 id="visibilite">4. Qui voit quoi sur le site</H2>
    <Ul>
      <li><strong>Professeurs :</strong> leur prénom, nom, photo et description sont publics (pages « Nos professeurs » et fiche du professeur), ainsi que leurs cours. L'adresse e-mail du professeur est associée à ses cours dans notre base de données.</li>
      <li><strong>Élèves :</strong> ton profil n'est pas public. Les autres utilisateurs ne voient pas ta date de naissance ni ton solde dans le site.</li>
      <li>Dans une visio, les autres participants voient le nom que tu utilises, ton image et entendent ta voix si tu actives caméra et micro.</li>
    </Ul>

    <H2 id="destinataires">5. Nos prestataires</H2>
    <Ul>
      <li><strong>Google (Firebase)</strong> — Google Ireland Limited, Irlande : hébergement du site, base de données, stockage des photos, connexion avec Google.</li>
      <li><strong>PayPal</strong> — PayPal (Europe) S.à r.l. et Cie, S.C.A., Luxembourg : paiement des pièces. PayPal agit comme responsable de ses propres traitements (voir sa politique de confidentialité).</li>
      <li><strong>Jitsi Meet</strong> (meet.jit.si) — 8x8, Inc., États-Unis : salles de visio Tatie Soso, quand tu rejoins un cours.</li>
      <li><strong>Autres outils de visio</strong> (Google Meet, Zoom…) : seulement si le professeur a choisi son propre lien, selon les règles de ce service.</li>
    </Ul>
    <P>
      Certains de ces prestataires peuvent traiter des données en dehors de l'Union européenne, notamment aux
      États-Unis. Ces transferts sont encadrés par le cadre de protection des données UE–États-Unis (Data Privacy
      Framework) ou par les clauses contractuelles types de la Commission européenne.
    </P>

    <H2 id="conservation">6. Combien de temps</H2>
    <Ul>
      <li>Données du compte et du profil : tant que ton compte est ouvert, puis supprimées dans les 30 jours après sa fermeture.</li>
      <li>Achats, paiements et inscriptions payées : 7 ans, comme l'exige la loi comptable belge.</li>
      <li>Journaux techniques : selon les durées de notre hébergeur, en général quelques semaines.</li>
    </Ul>

    <H2 id="mineurs">7. Les mineurs</H2>
    <P>
      Tatie Soso accueille des élèves mineurs. En dessous de 13 ans, le compte doit être créé par le parent ou le
      tuteur légal ; de 13 à 17 ans, l'inscription demande son accord. Les parents peuvent exercer les droits décrits
      ci-dessous pour leur enfant en nous écrivant à <Email />.
    </P>

    <H2 id="droits">8. Tes droits</H2>
    <P>Tu peux à tout moment :</P>
    <Ul>
      <li>accéder à tes données et en recevoir une copie (bouton « Télécharger mes données » sur la page Mon compte) ;</li>
      <li>les corriger (bouton « Modifier le profil ») ;</li>
      <li>demander leur effacement et la fermeture de ton compte ;</li>
      <li>t'opposer à un traitement ou demander sa limitation ;</li>
      <li>recevoir tes données dans un format réutilisable (portabilité).</li>
    </Ul>
    <P>
      Écris-nous à <Email />. Nous répondons dans un délai d'un mois. Si tu estimes que tes droits ne sont pas
      respectés, tu peux porter plainte auprès de l'Autorité de protection des données, rue de la Presse 35, 1000
      Bruxelles — <Ext href="https://www.autoriteprotectiondonnees.be">www.autoriteprotectiondonnees.be</Ext>.
    </P>

    <H2 id="cookies">9. Cookies et stockage dans ton navigateur</H2>
    <P>Tatie Soso n'utilise aucun cookie publicitaire ni de mesure d'audience. Seuls des éléments nécessaires au fonctionnement sont enregistrés :</P>
    <Ul>
      <li><strong>Connexion</strong> (Firebase, stockage du navigateur) : te garder connecté·e.</li>
      <li><strong>ts_lang</strong> (stockage local) : retenir la langue choisie.</li>
      <li><strong>ts_splash_seen</strong> (stockage de session) : ne montrer l'animation du logo qu'une fois par visite.</li>
      <li><strong>PayPal</strong> : ses cookies ne sont déposés que sur la page Portefeuille, quand tu paies.</li>
      <li><strong>Jitsi Meet</strong> : uniquement quand tu ouvres une salle de visio.</li>
    </Ul>
    <P>Comme ces éléments sont strictement nécessaires au service que tu demandes, ils ne nécessitent pas de consentement.</P>

    <H2 id="securite">10. Sécurité</H2>
    <P>
      Les échanges avec le site sont chiffrés (HTTPS) et l'accès aux données est limité par des règles de sécurité.
      En cas de violation de données présentant un risque pour toi, nous te prévenons et informons l'Autorité de
      protection des données dans les délais prévus par la loi.
    </P>

    <H2 id="changements">11. Modifications</H2>
    <P>Cette politique peut évoluer. La date de dernière mise à jour figure en haut de la page ; en cas de changement important, nous t'en informons sur le site.</P>
  </LegalLayout>
);
